import React, {useEffect, useState, useCallback} from 'react';
import {Avatar, Box, CircularProgress} from '@mui/material';
import {Button, Icon} from '@lightricks/react-design-system';
import Skeleton from '@/components/skeleton';
import {FileObject, FileStatus} from '@/stores/contentStudioStore';

interface ThumbnailDisplayProps {
  file?: FileObject;
  thumbnail?: string;
  onClose?: () => void;
  height?: number;
  width?: number;
  isLoading?: boolean;
  loadingType?: 'spinner' | 'shimmer';
}

const calculateThumbnailSize = (
  contentWidth: number,
  contentHeight: number,
  displayWidth: number,
  displayHeight: number
) => {
  return contentWidth < contentHeight
    ? {
        width: displayWidth,
        height: (contentHeight / contentWidth) * displayWidth,
      }
    : {
        width: (contentWidth / contentHeight) * displayHeight,
        height: displayHeight,
      };
};

function ThumbnailDisplay({
  file,
  thumbnail,
  onClose,
  width = 120,
  height = 120,
  isLoading = false,
  loadingType = 'spinner',
}: ThumbnailDisplayProps) {
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(
    null
  );
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>(
    thumbnail
  );

  // Keep thumbnail URL in sync with props
  useEffect(() => {
    // If we have a local file (uploading) – create object URL
    if (file?.file) {
      const url = URL.createObjectURL(file.file);
      setThumbnailUrl(url);

      // Revoke URL on cleanup to avoid leaks
      return () => URL.revokeObjectURL(url);
    }

    // Otherwise use the provided thumbnail (ready asset)
    if (thumbnail) {
      setThumbnailUrl(thumbnail);
    }

    return undefined;
  }, [file, thumbnail]);

  // Spinner shows while uploading; honours explicit `isLoading` prop when provided.
  const isUploading =
    typeof isLoading === 'boolean'
      ? isLoading
      : !!file && file.status !== FileStatus.SUCCESS;

  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    setVideoElement(node);
  }, []);

  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasElement(node);
  }, []);

  useEffect(() => {
    if (!videoElement || !canvasElement) {
      return;
    }

    const func = (
      now: number,
      metadata: {
        mediaTime: number;
        presentedFrames: number;
        width: number;
        height: number;
      }
    ) => {
      if (metadata.presentedFrames >= 10) {
        videoElement.pause();
        videoElement.currentTime = metadata.mediaTime;
        const ctx = canvasElement.getContext('2d');
        const {width: calculatedWidth, height: calculatedHeight} =
          calculateThumbnailSize(
            videoElement.videoWidth,
            videoElement.videoHeight,
            width + 10,
            height + 10
          );
        if (ctx) {
          canvasElement.width = calculatedWidth;
          canvasElement.height = calculatedHeight;
          ctx.drawImage(videoElement, 0, 0, calculatedWidth, calculatedHeight);
        }
      } else {
        videoElement.requestVideoFrameCallback(func);
      }
    };
    videoElement.requestVideoFrameCallback(func);
  }, [videoElement, canvasElement, height, width]);

  return (
    <Box sx={{position: 'relative'}}>
      <Avatar
        variant="rounded"
        sx={{width: `${width}px`, height: `${height}px`}}
      >
        {file ? (
          <>
            <video
              ref={videoRef}
              src={thumbnailUrl}
              autoPlay
              muted
              width={0}
              height={0}
            />
            <canvas
              ref={canvasRef}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </>
        ) : (
          <img
            src={thumbnailUrl}
            alt=""
            width={`${width}px`}
            height={`${height}px`}
            style={{objectFit: 'cover'}}
          />
        )}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor:
                loadingType === 'shimmer'
                  ? 'transparent'
                  : 'rgba(0, 0, 0, 0.4)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {loadingType === 'spinner' ? (
              <CircularProgress sx={{color: 'white'}} size={width / 2} />
            ) : (
              <Skeleton
                variant="rectangular"
                width={width}
                height={height}
                animation="wave"
                sx={{
                  'position': 'absolute',
                  'top': 0,
                  'left': 0,
                  'borderRadius': '4px',
                  '&.MuiSkeleton-wave::after': {
                    background:
                      'linear-gradient(to left, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0) 60%)',
                  },
                }}
              />
            )}
          </Box>
        )}
      </Avatar>
      {onClose && (
        <Button
          appearance="neutral"
          mode="elevated"
          size="small"
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            zIndex: 10,
            boxShadow:
              '0px 12px 42px -4px rgba(24, 39, 75, 0.12), 0px 4px 16px -6px rgba(24, 39, 75, 0.12)',
          }}
          icon={
            <Icon
              name="Actions-Close-Small"
              size="medium"
              appearance="primary"
            />
          }
          onClick={onClose}
        />
      )}
    </Box>
  );
}

export default ThumbnailDisplay;
